import React, { useEffect, useMemo, useState } from "react";
import {
  getFasceOrarie,
  createFasciaOraria,
  deleteFasciaOraria,
  updateFasciaOraria,
  getMieFasceOrarie,
  createMiaFasciaOraria,
  deleteMiaFasciaOraria,
  updateMiaFasciaOraria,
} from "./adminApi";
import ui from "../pages/ServiziCatalogoShared.module.css";

const giorniSettimana = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

function toMin(hhmm) {
  const [h, m] = String(hhmm || "00:00")
    .split(":")
    .map(Number);
  return (h || 0) * 60 + (m || 0);
}

function overlap(s1, e1, s2, e2) {
  return Math.max(s1, s2) < Math.min(e1, e2);
}

function formatTipo(tipo) {
  if (tipo === "ritiro") return "Ritiro";
  if (tipo === "consegna") return "Consegna";
  return "Entrambi";
}

export default function FasceOrarie({ idFornitore = null, title }) {
  const token = localStorage.getItem("token");
  const [fasce, setFasce] = useState([]);
  const [filterDay, setFilterDay] = useState("Tutti");
  const [loading, setLoading] = useState(true);

  const isAdminMode = idFornitore != null && idFornitore !== "";

  const [newFascia, setNewFascia] = useState({
    giorno_settimana: "Lunedì",
    orario_inizio: "09:00",
    orario_fine: "12:00",
    tipo: "ritiro",
  });

  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({
    giorno_settimana: "Lunedì",
    orario_inizio: "09:00",
    orario_fine: "12:00",
    tipo: "ritiro",
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = isAdminMode
        ? await getFasceOrarie(idFornitore, token)
        : await getMieFasceOrarie(token);
      setFasce(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFornitore]);

  const listSorted = useMemo(() => {
    const idx = (g) => giorniSettimana.indexOf(g);

    const rows = [...(fasce || [])].sort((a, b) => {
      const da = idx(a.giorno_settimana);
      const db = idx(b.giorno_settimana);
      if (da !== db) return da - db;
      return toMin(a.orario_inizio) - toMin(b.orario_inizio);
    });

    return filterDay === "Tutti"
      ? rows
      : rows.filter((r) => r.giorno_settimana === filterDay);
  }, [fasce, filterDay]);

  const validate = (entry, excludeId = null) => {
    const { giorno_settimana, orario_inizio, orario_fine } = entry;

    if (!giorniSettimana.includes(giorno_settimana)) {
      alert("Giorno non valido");
      return false;
    }
    if (!orario_inizio || !orario_fine) {
      alert("Specifica orario inizio e fine");
      return false;
    }
    if (orario_inizio >= orario_fine) {
      alert("Fine > Inizio");
      return false;
    }

    const sNew = toMin(orario_inizio);
    const eNew = toMin(orario_fine);

    const sameDay = fasce.filter(
      (f) => f.giorno_settimana === giorno_settimana && f.id !== excludeId
    );

    const clash = sameDay.some((f) =>
      overlap(sNew, eNew, toMin(f.orario_inizio), toMin(f.orario_fine))
    );

    if (clash) {
      alert("Sovrapposizione con un'altra fascia in quel giorno");
      return false;
    }

    return true;
  };

  const handleAdd = async () => {
    if (!validate(newFascia)) return;

    if (isAdminMode) {
      await createFasciaOraria(
        { ...newFascia, id_fornitore: Number(idFornitore) },
        token
      );
    } else {
      await createMiaFasciaOraria(newFascia, token);
    }

    await load();
  };

  const startEdit = (f) => {
    setEditId(f.id);
    setEditData({
      giorno_settimana: f.giorno_settimana,
      orario_inizio: f.orario_inizio,
      orario_fine: f.orario_fine,
      tipo: f.tipo,
    });
  };

  const cancelEdit = () => {
    setEditId(null);
  };

  const saveEdit = async () => {
    const payload = { ...editData };
    if (!validate(payload, editId)) return;

    if (isAdminMode) {
      await updateFasciaOraria(editId, payload, token);
    } else {
      await updateMiaFasciaOraria(editId, payload, token);
    }

    setEditId(null);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare fascia oraria?")) return;

    if (isAdminMode) {
      await deleteFasciaOraria(id, token);
    } else {
      await deleteMiaFasciaOraria(id, token);
    }

    await load();
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h4 className={ui.cardTitle} style={{ marginBottom: 6 }}>
              {title ||
                (isAdminMode
                  ? "Fasce orarie del fornitore"
                  : "Le mie fasce orarie")}
            </h4>
            <div className={ui.hint}>
              Configura gli orari in cui effettui ritiro, consegna o entrambi.
            </div>
          </div>

          <div style={{ minWidth: 220, maxWidth: 260, width: "100%" }}>
            <label className={ui.label}>Filtra per giorno</label>
            <select
              className={ui.input}
              value={filterDay}
              onChange={(e) => setFilterDay(e.target.value)}
            >
              <option value="Tutti">Tutti i giorni</option>
              {giorniSettimana.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ marginBottom: 18 }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(180px,1.3fr) minmax(120px,1fr) minmax(120px,1fr) minmax(150px,1fr) auto",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <label className={ui.label}>Giorno</label>
            <select
              className={ui.input}
              value={newFascia.giorno_settimana}
              onChange={(e) =>
                setNewFascia((s) => ({
                  ...s,
                  giorno_settimana: e.target.value,
                }))
              }
            >
              {giorniSettimana.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={ui.label}>Da</label>
            <input
              className={ui.input}
              type="time"
              value={newFascia.orario_inizio}
              onChange={(e) =>
                setNewFascia((s) => ({
                  ...s,
                  orario_inizio: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className={ui.label}>A</label>
            <input
              className={ui.input}
              type="time"
              value={newFascia.orario_fine}
              onChange={(e) =>
                setNewFascia((s) => ({
                  ...s,
                  orario_fine: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className={ui.label}>Tipo</label>
            <select
              className={ui.input}
              value={newFascia.tipo}
              onChange={(e) =>
                setNewFascia((s) => ({ ...s, tipo: e.target.value }))
              }
            >
              <option value="ritiro">Ritiro</option>
              <option value="consegna">Consegna</option>
              <option value="entrambi">Entrambi</option>
            </select>
          </div>

          <div>
            <button
              type="button"
              className={ui.btnPrimary}
              onClick={handleAdd}
            >
              Aggiungi
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className={ui.hint}>Caricamento fasce orarie...</div>
      ) : listSorted.length === 0 ? (
        <div className={ui.hint}>Nessuna fascia oraria</div>
      ) : (
        <>
          <div className={ui.tableDesktop}>
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <colgroup>
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "28%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={ui.th}>Giorno</th>
                    <th className={ui.th}>Orario</th>
                    <th className={ui.th}>Tipo</th>
                    <th className={`${ui.th} ${ui.thActions}`}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {listSorted.map((f) => (
                    <tr key={f.id}>
                      {editId === f.id ? (
                        <>
                          <td className={ui.td}>
                            <select
                              className={ui.input}
                              value={editData.giorno_settimana}
                              onChange={(e) =>
                                setEditData((s) => ({
                                  ...s,
                                  giorno_settimana: e.target.value,
                                }))
                              }
                            >
                              {giorniSettimana.map((g) => (
                                <option key={g} value={g}>
                                  {g}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className={ui.td}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                              }}
                            >
                              <input
                                className={ui.input}
                                type="time"
                                value={editData.orario_inizio}
                                onChange={(e) =>
                                  setEditData((s) => ({
                                    ...s,
                                    orario_inizio: e.target.value,
                                  }))
                                }
                              />
                              <input
                                className={ui.input}
                                type="time"
                                value={editData.orario_fine}
                                onChange={(e) =>
                                  setEditData((s) => ({
                                    ...s,
                                    orario_fine: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </td>

                          <td className={ui.td}>
                            <select
                              className={ui.input}
                              value={editData.tipo}
                              onChange={(e) =>
                                setEditData((s) => ({
                                  ...s,
                                  tipo: e.target.value,
                                }))
                              }
                            >
                              <option value="ritiro">Ritiro</option>
                              <option value="consegna">Consegna</option>
                              <option value="entrambi">Entrambi</option>
                            </select>
                          </td>

                          <td className={`${ui.td} ${ui.tdActions}`}>
                            <div className={ui.actions}>
                              <button
                                type="button"
                                className={ui.btnPrimary}
                                onClick={saveEdit}
                              >
                                Salva
                              </button>
                              <button
                                type="button"
                                className={ui.btnSecondary}
                                onClick={cancelEdit}
                              >
                                Annulla
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className={ui.td}>{f.giorno_settimana}</td>
                          <td className={ui.td}>
                            {f.orario_inizio} – {f.orario_fine}
                          </td>
                          <td className={ui.td}>{formatTipo(f.tipo)}</td>
                          <td className={`${ui.td} ${ui.tdActions}`}>
                            <div className={ui.actions}>
                              <button
                                type="button"
                                className={ui.btnSecondary}
                                onClick={() => startEdit(f)}
                              >
                                Modifica
                              </button>
                              <button
                                type="button"
                                className={ui.btnDanger}
                                onClick={() => handleDelete(f.id)}
                              >
                                Elimina
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={ui.mobileList}>
            {listSorted.map((f) => (
              <article key={f.id} className={ui.mobileCard}>
                {editId === f.id ? (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div>
                        <label className={ui.label}>Giorno</label>
                        <select
                          className={ui.input}
                          value={editData.giorno_settimana}
                          onChange={(e) =>
                            setEditData((s) => ({
                              ...s,
                              giorno_settimana: e.target.value,
                            }))
                          }
                        >
                          {giorniSettimana.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div>
                          <label className={ui.label}>Da</label>
                          <input
                            className={ui.input}
                            type="time"
                            value={editData.orario_inizio}
                            onChange={(e) =>
                              setEditData((s) => ({
                                ...s,
                                orario_inizio: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className={ui.label}>A</label>
                          <input
                            className={ui.input}
                            type="time"
                            value={editData.orario_fine}
                            onChange={(e) =>
                              setEditData((s) => ({
                                ...s,
                                orario_fine: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <label className={ui.label}>Tipo</label>
                        <select
                          className={ui.input}
                          value={editData.tipo}
                          onChange={(e) =>
                            setEditData((s) => ({
                              ...s,
                              tipo: e.target.value,
                            }))
                          }
                        >
                          <option value="ritiro">Ritiro</option>
                          <option value="consegna">Consegna</option>
                          <option value="entrambi">Entrambi</option>
                        </select>
                      </div>
                    </div>

                    <div className={ui.mobileActions}>
                      <button
                        type="button"
                        className={ui.btnPrimary}
                        onClick={saveEdit}
                      >
                        Salva
                      </button>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        onClick={cancelEdit}
                      >
                        Annulla
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={ui.mobileCardTop}>
                      <div className={ui.mobileTitleWrap}>
                        <h4 className={ui.mobileTitle}>{f.giorno_settimana}</h4>
                        <p className={ui.mobileSub}>
                          {f.orario_inizio} – {f.orario_fine}
                        </p>
                      </div>
                      <span className={`${ui.mobileBadge} ${ui.mobileBadgeOn}`}>
                        {formatTipo(f.tipo)}
                      </span>
                    </div>

                    <div className={ui.mobileStats}>
                      <div className={ui.mobileStat}>
                        <span className={ui.mobileStatLabel}>Giorno</span>
                        <span className={ui.mobileStatValue}>
                          {f.giorno_settimana}
                        </span>
                      </div>
                      <div className={ui.mobileStat}>
                        <span className={ui.mobileStatLabel}>Orario</span>
                        <span className={ui.mobileStatValue}>
                          {f.orario_inizio} – {f.orario_fine}
                        </span>
                      </div>
                    </div>

                    <div className={ui.mobileActions}>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        onClick={() => startEdit(f)}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        className={ui.btnDanger}
                        onClick={() => handleDelete(f.id)}
                      >
                        Elimina
                      </button>
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}